import React, { useMemo } from 'react';
import { ChevronRight, Calendar } from 'lucide-react';

const GanttView = ({ tasks, onTaskClick }) => {
  // Calculate timeline bounds
  const { startDate, endDate, monthsRange } = useMemo(() => {
    if (!tasks || tasks.length === 0) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 3, 0);
      return { startDate: start, endDate: end, monthsRange: [] };
    }

    // Get all dates from timeline field (format: "YYYY-MM-DD to YYYY-MM-DD")
    const dates = [];
    tasks.forEach(t => {
      if (t.timeline && typeof t.timeline === 'string') {
        const parts = t.timeline.split(' to ');
        if (parts.length === 2) {
          dates.push(new Date(parts[0].trim()));
          dates.push(new Date(parts[1].trim()));
        }
      } else if (t.date) {
        dates.push(new Date(t.date));
      }
    });
    
    if (dates.length === 0) {
      const today = new Date();
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 3, 0);
      return { startDate: start, endDate: end, monthsRange: [] };
    }

    const earliest = new Date(Math.min(...dates));
    const latest = new Date(Math.max(...dates));
    
    // Add 1 month padding on each side
    const start = new Date(earliest.getFullYear(), earliest.getMonth() - 1, 1);
    const end = new Date(latest.getFullYear(), latest.getMonth() + 2, 0);
    
    // Generate months range
    const months = [];
    const current = new Date(start);
    while (current <= end) {
      months.push({
        date: new Date(current),
        label: current.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }),
        days: new Date(current.getFullYear(), current.getMonth() + 1, 0).getDate()
      });
      current.setMonth(current.getMonth() + 1);
    }

    return { startDate: start, endDate: end, monthsRange: months };
  }, [tasks]);

  // Calculate position and width for a task bar
  const getTaskBarStyle = (task) => {
    let taskStartDate = null;
    let taskEndDate = null;
    
    // Parse timeline field ("YYYY-MM-DD to YYYY-MM-DD")
    if (task.timeline && typeof task.timeline === 'string') {
      const parts = task.timeline.split(' to ');
      if (parts.length === 2) {
        taskStartDate = new Date(parts[0].trim());
        taskEndDate = new Date(parts[1].trim());
      }
    }
    
    // Fallback to date field if no timeline
    if (!taskStartDate && !taskEndDate && task.date) {
      taskEndDate = new Date(task.date);
      taskStartDate = new Date(taskEndDate.getTime() - 7 * 24 * 60 * 60 * 1000); // 7 days before
    }
    
    if (!taskStartDate || !taskEndDate) return null;
    
    const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    const daysFromStart = Math.ceil((taskStartDate - startDate) / (1000 * 60 * 60 * 24));
    const taskDuration = Math.ceil((taskEndDate - taskStartDate) / (1000 * 60 * 60 * 24)) || 1;
    
    const left = (daysFromStart / totalDays) * 100;
    const width = (taskDuration / totalDays) * 100;
    
    return {
      left: `${Math.max(0, Math.min(95, left))}%`,
      width: `${Math.max(1, Math.min(50, width))}%`
    };
  };

  const getStatusColor = (status) => {
    const colors = {
      'to_do': '#C4C4C4',
      'in_progress': '#FDAB3D',
      'review': '#579BFC',
      'done': '#00C875'
    };
    return colors[status.value] || colors[status.label?.toLowerCase().replace(/\s+/g, '_')] || '#C4C4C4';
  };

  if (!tasks || tasks.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
        <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">No tasks to display in Gantt view</p>
        <p className="text-sm text-gray-500 mt-2">Add tasks with timeline to see the Gantt chart</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      {/* Header with months */}
      <div className="border-b bg-gray-50">
        <div className="flex">
          {/* Task names column */}
          <div className="w-64 border-r p-3 font-semibold text-sm text-gray-700 bg-white sticky left-0 z-10">
            Tasks
          </div>
          
          {/* Timeline header */}
          <div className="flex-1 flex">
            {monthsRange.map((month, idx) => (
              <div
                key={idx}
                className="border-r last:border-r-0 px-2 py-3 text-center"
                style={{ minWidth: `${100 / monthsRange.length}%` }}
              >
                <div className="text-xs font-semibold text-gray-700">
                  {month.label}
                </div>
                <div className="text-[10px] text-gray-500 mt-1">
                  {month.days} days
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Task rows */}
      <div className="max-h-[600px] overflow-y-auto">
        {tasks.map((task) => {
          const barStyle = getTaskBarStyle(task);
          
          return (
            <div
              key={task._id}
              className="flex border-b last:border-b-0 hover:bg-gray-50 transition-colors"
            >
              {/* Task name column */}
              <div className="w-64 border-r p-3 sticky left-0 bg-white z-10">
                <button
                  onClick={() => {
                    if (onTaskClick && typeof onTaskClick === 'function') {
                      onTaskClick(task);
                    }
                  }}
                  className="text-left w-full hover:text-[#1C3E53] transition-colors"
                >
                  <div className="flex items-center gap-2">
                    <ChevronRight className="h-3 w-3 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {task.task}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="text-[10px] px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: `${getStatusColor(task.status)}20`,
                            color: getStatusColor(task.status)
                          }}
                        >
                          {task.status.label}
                        </span>
                        {task.progress !== null && task.progress !== undefined && (
                          <span className="text-[10px] text-gray-500">
                            {task.progress}%
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Timeline area */}
              <div className="flex-1 relative" style={{ minHeight: '60px' }}>
                {/* Today indicator */}
                {(() => {
                  const today = new Date();
                  const totalDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
                  const daysFromStart = Math.ceil((today - startDate) / (1000 * 60 * 60 * 24));
                  const todayPosition = (daysFromStart / totalDays) * 100;
                  
                  if (todayPosition >= 0 && todayPosition <= 100) {
                    return (
                      <div
                        className="absolute top-0 bottom-0 w-[2px] bg-red-400 opacity-30 z-0"
                        style={{ left: `${todayPosition}%` }}
                      />
                    );
                  }
                  return null;
                })()}

                {/* Task bar */}
                {barStyle && (
                  <div
                    className="absolute top-1/2 transform -translate-y-1/2 rounded z-10 cursor-pointer hover:shadow-lg transition-all group"
                    style={{
                      ...barStyle,
                      height: task.progress ? '24px' : '20px',
                      backgroundColor: getStatusColor(task.status),
                    }}
                    onClick={() => {
                      if (onTaskClick && typeof onTaskClick === 'function') {
                        onTaskClick(task);
                      }
                    }}
                  >
                    {/* Progress bar inside */}
                    {task.progress !== null && task.progress !== undefined && (
                      <div
                        className="h-full bg-white/30 rounded"
                        style={{ width: `${task.progress}%` }}
                      />
                    )}
                    
                    {/* Tooltip on hover */}
                    <div className="absolute left-0 top-full mt-2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-20">
                      {task.timeline || task.date} {task.progress ? `• ${task.progress}%` : ''}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="border-t bg-gray-50 px-4 py-3 flex items-center gap-6 text-xs">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-gray-300"></div>
          <span className="text-gray-600">To Do</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#FDAB3D]"></div>
          <span className="text-gray-600">In Progress</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#579BFC]"></div>
          <span className="text-gray-600">Review</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded bg-[#00C875]"></div>
          <span className="text-gray-600">Done</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <div className="w-[2px] h-3 bg-red-400"></div>
          <span className="text-gray-600">Today</span>
        </div>
      </div>
    </div>
  );
};

export default GanttView;
